import { getFormProps, getInputProps, useForm } from '@conform-to/react'
import {
  json,
  redirect,
  type DataFunctionArgs,
  type MetaFunction,
} from '@remix-run/node'
import {
  Form,
  useActionData,
  useLoaderData,
  useNavigation,
} from '@remix-run/react'
import { z } from 'zod'
import { GeneralErrorBoundary } from '~/components/error-boundary.tsx'
import { AuthLayout } from '~/components/layout/auth-layout.tsx'
import { Spacer } from '~/components/ui/spacer.tsx'
import { StatusButton } from '~/components/ui/inputs/button/status-button.tsx'
import {
  authenticator,
  requireAnonymous,
  resetUserPassword,
} from '~/features/auth/auth.server.ts'
import { commitSession, getSession } from '~/utils/config/session.server.ts'
import { passwordSchema } from '~/utils/user-validation.ts'
import { ErrorList, Field } from '~/components/ui/inputs/forms.tsx'
import { getZodConstraint, parseWithZod } from '@conform-to/zod'
import { useIsPending } from '~/utils/misc.tsx'

export const resetPasswordEmailSessionKey = 'resetPasswordEmail'

const ResetPasswordSchema = z
  .object({
    password: passwordSchema,
    confirmPassword: passwordSchema,
  })
  .refine(({ confirmPassword, password }) => password === confirmPassword, {
    message: 'The passwords did not match',
    path: ['confirmPassword'],
  })

export async function loader({ request }: DataFunctionArgs) {
  await requireAnonymous(request)
  const session = await getSession(request.headers.get('cookie'))
  const error = session.get(authenticator.sessionErrorKey)
  const resetPasswordEmail = session.get(resetPasswordEmailSessionKey)
  if (typeof resetPasswordEmail !== 'string' || !resetPasswordEmail) {
    return redirect('/login')
  }
  return json(
    { formError: error?.message, resetPasswordEmail },
    { headers: { 'Set-Cookie': await commitSession(session) } },
  )
}

export async function action({ request }: DataFunctionArgs) {
  const formData = await request.formData()
  const submission = parseWithZod(formData, {
    schema: ResetPasswordSchema,
  })
  if (submission.status !== 'success') {
    return json(
      { result: submission.reply() },
      { status: submission.status === 'error' ? 400 : 200 },
    )
  }
  const { password } = submission.value

  const session = await getSession(request.headers.get('cookie'))
  const email = session.get(resetPasswordEmailSessionKey)
  if (typeof email !== 'string' || !email) {
    return redirect('/login')
  }
  await resetUserPassword({ email, password })
  session.unset(resetPasswordEmailSessionKey)
  return redirect('/login', {
    headers: { 'Set-Cookie': await commitSession(session) },
  })
}

export const meta: MetaFunction = () => {
  return [{ title: 'Reset Password | ITraack' }]
}

export default function ResetPasswordPage() {
  const data = useLoaderData<typeof loader>()
  const actionData = useActionData<typeof action>()
  const navigation = useNavigation()
  const isPending = useIsPending()

  const [form, fields] = useForm({
    id: 'reset-password',
    constraint: getZodConstraint(ResetPasswordSchema),
    lastResult: actionData?.result,
    onValidate({ formData }) {
      return parseWithZod(formData, { schema: ResetPasswordSchema })
    },
    shouldRevalidate: 'onBlur',
  })

  return (
    <AuthLayout>
      <h1 className="text-h4">Establecer nueva contraseña</h1>
      <p className="text-body-md mt-3 text-foreground-muted">
        Hola, {data.resetPasswordEmail}. No te preocupes. Esto sucede todo el
        tiempo.
      </p>
      <Spacer size="4xs" />
      <Form method="POST" {...getFormProps(form)}>
        <Field
          labelProps={{
            htmlFor: fields.password.id,
            children: 'Nueva contraseña',
          }}
          inputProps={{
            ...getInputProps(fields.password, { type: 'password' }),
            autoComplete: 'new-password',
            autoFocus: true,
          }}
          errors={fields.password.errors}
        />
        <Spacer size="4xs" />
        <Field
          labelProps={{
            htmlFor: fields.confirmPassword.id,
            children: 'Confirmar contraseña',
          }}
          inputProps={{
            ...getInputProps(fields.confirmPassword, { type: 'password' }),
            autoComplete: 'new-password',
          }}
          errors={fields.confirmPassword.errors}
        />

        <ErrorList errors={form.errors} id={form.errorId} />

        <Spacer size="3xs" />
        <StatusButton
          className="w-full"
          status={isPending ? 'pending' : form.status ?? 'idle'}
          type="submit"
          disabled={navigation.state !== 'idle'}
        >
          Reestablecer contraseña
        </StatusButton>
      </Form>
    </AuthLayout>
  )
}

export function ErrorBoundary() {
  return <GeneralErrorBoundary />
}
